/**
 * DemoDataBadge — small yellow pill `[ DEMO DATA ]` for `isTemplate` packages.
 *
 * GH-268 Phase 4. Renders wherever a sample-pack template package is shown
 * (list cards, package home header, dashboard counters when toggle is on).
 *
 * Tooltip via native `title` attr: "Sample data — safe to delete. Cleanup in
 * Settings." Aria-label exposes the same intent for screen readers.
 *
 * i18n: keys live under the `packages` namespace and are introduced in Phase 5.
 * Until then, react-i18next will fall back to the key string, which is still
 * legible enough during dev.
 */

import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'

interface DemoDataBadgeProps {
  /** Optional className override for sizing/spacing in different surfaces. */
  className?: string
  /** Compact mode for use inside small-card density (e.g. counter chips). */
  size?: 'sm' | 'md'
}

export function DemoDataBadge({ className, size = 'md' }: DemoDataBadgeProps) {
  const { t } = useTranslation('packages')

  const padding =
    size === 'sm' ? 'px-1.5 py-0 text-[0.625rem]' : 'px-2 py-0.5 text-[0.6875rem]'

  return (
    <span
      role="status"
      aria-label={t('demoBadge.ariaLabel')}
      title={t('demoBadge.tooltip')}
      data-testid="demo-data-badge"
      className={cn(
        'inline-flex select-none items-center gap-1 rounded-full font-semibold uppercase tracking-wider',
        'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-300/60',
        'dark:bg-yellow-900/30 dark:text-yellow-200 dark:ring-yellow-400/30',
        padding,
        className,
      )}
    >
      {t('demoBadge.label')}
    </span>
  )
}
